import classnames from "classnames";
import { inject } from "mobx-react";
import { Fragment } from "react";
import { DateDisplay } from "../../../../core/components/DateDisplay.component";
import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import { ContentTileProps } from "../../../../core/components/tiles/ContentTile.component";
import { canHaveOps, generateImageAssetSizes } from "@inferno/renderer-shared-core";
import { placeholderImage } from "@ihr-radioedit/inferno-core";
import { DATETIME_DISPLAY_FORMATS } from "@inferno/renderer-shared-core";
import { MagicLink } from "../../../../core/ui";
import { RESPONSIVE_IMAGE } from "../../../../core/ui/constants";
import styles from "./CoastFeedCalendarItem.module.scss";

export interface CoastFeedCalendarItemProps extends ContentTileProps {
  startDateTime?: number;
  endDateTime?: number;
  dateFormat?: string;
  venueName?: string;
  venueAddress?: string;
  showDateStamp?: boolean;
  hideEventDate?: boolean;
}

export const CoastFeedCalendarItem = inject("store")((props: CoastFeedCalendarItemProps) => {
  if (!props.store) {
    return null;
  }

  const { site, env } = props.store;
  const { startDateTime, endDateTime, venueName, venueAddress, showDateStamp, hideEventDate } = props;
  const tileClass = classnames([styles.componentContentTile, { [`${props.className}`]: props.className }], {
    external: !canHaveOps(props.thumb),
  });

  const target = props.target || "_self";
  const { srcset, sizes } = generateImageAssetSizes(props.thumb);
  const placeholderSrc = `${placeholderImage}?ops=resize(900,506),quality(10)`;

  const eventDates =
    endDateTime && startDateTime === endDateTime ? (
      <Fragment>
        <DateDisplay
          datetime={startDateTime}
          dateFormat={DATETIME_DISPLAY_FORMATS.FULL_MONTH_SHORT_DAY}
          timezone={site?.index.timeZone || env.DEFAULT_TIMEZONE}
          className={styles.eventDate}
        />
        &nbsp;-&nbsp;
        <DateDisplay
          datetime={endDateTime}
          dateFormat={DATETIME_DISPLAY_FORMATS.SHORT_DAY_FULL_YEAR}
          timezone={site?.index.timeZone || env.DEFAULT_TIMEZONE}
          className={styles.eventDate}
        />
      </Fragment>
    ) : (
      <DateDisplay
        datetime={startDateTime}
        dateFormat={DATETIME_DISPLAY_FORMATS.FULL_MONTH_SHORT_DAY_FULL_YEAR}
        timezone={site?.index.timeZone || env.DEFAULT_TIMEZONE}
        className={styles.eventDate}
      />
    );

  return (
    <figure className={tileClass}>
      <div className={styles.thumbContainer}>
        <MagicLink to={props.url} target={target} context={props.slug} action={props.action}>
          <ResponsiveImage
            src={placeholderSrc}
            placeholder={placeholderSrc}
            alt={props.title}
            srcset={srcset}
            sizes={sizes}
            initialWidth={RESPONSIVE_IMAGE.imageAssetInitialWidth}
            initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
          />
        </MagicLink>
      </div>
      <figcaption>
        <MagicLink
          className={styles.cardTitle}
          to={props.url}
          target={target}
          context={props.slug}
          action={props.action}
        >
          {props.title}
        </MagicLink>
        {showDateStamp && !hideEventDate ? eventDates : null}
        {venueName ? <h3>{venueName}</h3> : null}
        {venueAddress ? <h3>{venueAddress}</h3> : null}
        <MagicLink className="more" to={props.url} target={target} context={props.slug} action={props.action}>
          More &raquo;
        </MagicLink>
      </figcaption>
    </figure>
  );
});

export default CoastFeedCalendarItem;
